import React, { useState, useRef } from 'react';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/vi';
import './DateRangePickerModal.scss';
import viVN from 'antd/lib/date-picker/locale/vi_VN';
import { CalendarNewIcon, ArrowRightDateIcon, Cancel01Icon } from '@assets/icon';

dayjs.locale('vi');

interface DateRangePickerModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (dateRange: string) => void;
  reportType?: string;
}

const PRESET_RANGES = [
  { label: 'Hôm nay', getRange: () => [dayjs(), dayjs()] },
  { label: 'Hôm qua', getRange: () => [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
  { label: 'Tháng này', getRange: () => [dayjs().startOf('month'), dayjs()] },
  { label: 'Tháng trước', getRange: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { label: '30 ngày', getRange: () => [dayjs().subtract(30, 'day'), dayjs()] },
  { label: 'Năm nay', getRange: () => [dayjs().startOf('year'), dayjs()] },
];

const formatDateRange = (start: Dayjs, end: Dayjs, reportType?: string): string => {
  const startStr = start.format('DD/MM/YYYY');
  const endStr = end.format('DD/MM/YYYY');
  const dateRange = startStr === endStr
    ? startStr
    : `từ ngày ${startStr} - ${endStr}`;
  if (reportType) {
    return `${reportType} ${dateRange}`;
  }
  return dateRange;
};

const pickerLocale = {
  ...viVN,
  lang: {
    ...(viVN as any).lang,
    monthFormat: '[Tháng] MM  /',
    monthBeforeYear: true,
    yearFormat: 'YYYY',
    cellYearFormat: 'YYYY',

    shortMonths: [
      'Tháng 01',
      'Tháng 02',
      'Tháng 03',
      'Tháng 04',
      'Tháng 05',
      'Tháng 06',
      'Tháng 07',
      'Tháng 08',
      'Tháng 09',
      'Tháng 10',
      'Tháng 11',
      'Tháng 12',
    ],
  },
};

interface ModalHeaderProps {
  onClose: () => void;
}
const ModalHeader: React.FC<ModalHeaderProps> = ({ onClose }) => (
  <div className="drpm-header">
    <h3 className="drpm-title">Bộ lọc</h3>
    <button className="drpm-close" onClick={() => onClose?.()} aria-label="Đóng" type="button">
      <Cancel01Icon style={{ width: '24px', height: '24px' }} />
    </button>
  </div>
);

const SectionLabel: React.FC = () => (
  <div className="drpm-section-label">
    <span className="drpm-section-divider" />
    <span className="drpm-section-title">THỜI GIAN</span>
  </div>
);

interface PresetGridProps {
  selectedPreset: string | null;
  onSelect: (preset: typeof PRESET_RANGES[0]) => void;
}
const PresetGrid: React.FC<PresetGridProps> = ({ selectedPreset, onSelect }) => (
  <div className="drpm-preset-grid">
    {PRESET_RANGES.map((preset) => (
      <button
        key={preset.label}
        type="button"
        className={`drpm-option-btn${selectedPreset === preset.label ? ' active' : ''}`}
        onClick={() => onSelect(preset)}
      >{preset.label}</button>
    ))}
  </div>
);

const CustomRowButton: React.FC<{ active: boolean; onClick: () => void }> = ({
  active,
  onClick,
}) => (
  <button
    type="button"
    className={`drpm-option-btn drpm-custom-row-btn${active ? ' active' : ''}`}
    onClick={onClick}
  >Tuỳ chọn thời gian</button>
);

interface DatePickerFieldProps {
  value: Dayjs | null;
  mode: any;
  onChange: (d: Dayjs | null) => void;
  onPanelChange: (_: any, mode: any) => void;
  disabledDate: (c: Dayjs) => boolean;
  getPopupContainer: () => HTMLElement;
  placeholder?: string;
}

const DatePickerField: React.FC<DatePickerFieldProps> = (p) => (
  <div className="drpm-unified-date-input">
    <DatePicker
      bordered={false} presets={[]} locale={pickerLocale as any}
      value={p?.value ?? null} mode={p?.mode} popupClassName="drpm-picker-popup"
      onChange={p?.onChange} onPanelChange={p?.onPanelChange}
      disabledDate={p?.disabledDate} format="DD/MM/YYYY"
      placeholder={p?.placeholder || 'Chọn ngày'} suffixIcon={null}
      getPopupContainer={p?.getPopupContainer}
    />
  </div>
);

interface UnifiedDateRowProps {
  containerRef?: React.Ref<HTMLDivElement>;
  startProps: DatePickerFieldProps;
  endProps: DatePickerFieldProps;
}
const UnifiedDateRow: React.FC<UnifiedDateRowProps> = ({ containerRef, startProps, endProps }) => (
  <div className="drpm-unified-date-row" ref={containerRef}>
    <DatePickerField {...startProps} placeholder="Từ ngày" />
    <span className="drpm-unified-date-separator">
      <ArrowRightDateIcon style={{ width: '20px', height: '20px' }} />
    </span>
    <DatePickerField {...endProps} placeholder="Đến ngày" />
    <span className="drpm-unified-date-icon">
      <CalendarNewIcon style={{ width: '20px', height: '20px' }} />
    </span>
  </div>
);

interface ModalActionsProps {
  canApply: boolean;
  onReset: () => void;
  onApply: () => void;
}
const ModalActions: React.FC<ModalActionsProps> = ({ canApply, onReset, onApply }) => (
  <div className="drpm-actions">
    <button type="button" className="drpm-btn-reset" onClick={onReset}>
      Thiết lập lại
    </button>
    <button
      type="button"
      className={`drpm-btn-confirm${!canApply ? ' disabled' : ''}`}
      onClick={onApply}
      disabled={!canApply}
    >Áp dụng</button>
  </div>
);

export const DateRangePickerModal: React.FC<DateRangePickerModalProps> = ({
  open,
  onClose,
  onConfirm,
  reportType,
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [showCustom, setShowCustom] = useState(true);
  const [customStart, setCustomStart] = useState<Dayjs | null>(null);
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(null);
  const [startPanelMode, setStartPanelMode] = useState<any>('date');
  const [endPanelMode, setEndPanelMode] = useState<any>('date');
  const containerRef = useRef<HTMLDivElement>(null);

  const canApply = selectedPreset !== null || (showCustom && customStart && customEnd);

  const handlePresetClick = (preset: typeof PRESET_RANGES[0]) => {
    setSelectedPreset(preset.label);
    setShowCustom(false);
  };

  const handleCustomClick = () => {
    setShowCustom(true);
    setSelectedPreset(null);
  };

  const handleReset = () => {
    setSelectedPreset(null);
    setShowCustom(false);
    setCustomStart(null);
    setCustomEnd(null);
  };

  const handleApply = () => {
    if (showCustom && customStart && customEnd) {
      const start = customStart.isBefore(customEnd) ? customStart : customEnd;
      const end = customStart.isBefore(customEnd) ? customEnd : customStart;
      onConfirm(formatDateRange(start, end, reportType));
    } else if (selectedPreset) {
      onConfirm(selectedPreset);
    }
    onClose();
  };

  const handleStartChange = (date: Dayjs | null) => {
    setCustomStart(date);
    if (customEnd && date && date.isAfter(customEnd)) setCustomEnd(null);
  };

  const handleEndChange = (date: Dayjs | null) => {
    setCustomEnd(date);
    if (customStart && date && date.isBefore(customStart)) setCustomStart(null);
  };

  const disabledStartDate = (cur: Dayjs) => (customEnd ? cur.isAfter(customEnd, 'day') : false);
  const disabledEndDate = (cur: Dayjs) => (customStart ? cur.isBefore(customStart, 'day') : false);
  const onStartPanelChange = (_: any, m: any) => { if (m !== 'date') setStartPanelMode('date'); };
  const onEndPanelChange = (_: any, m: any) => { if (m !== 'date') setEndPanelMode('date'); };
  const getPopupContainer = () => containerRef.current || document.body;

  if (!open) return null;

  return (
    <div className="drpm-fullscreen">
      <div className="drpm-content-wrapper">
        <ModalHeader onClose={onClose} />
        <div className="drpm-options-card">
          <SectionLabel />
          <div className="drpm-options-group">
            <PresetGrid selectedPreset={selectedPreset} onSelect={handlePresetClick} />
            <CustomRowButton active={showCustom} onClick={handleCustomClick} />
            {showCustom && (
              <UnifiedDateRow
                containerRef={containerRef}
                startProps={{
                  value: customStart, mode: startPanelMode, onChange: handleStartChange,
                  onPanelChange: onStartPanelChange, disabledDate: disabledStartDate,
                  getPopupContainer,
                }}
                endProps={{
                  value: customEnd, mode: endPanelMode, onChange: handleEndChange,
                  onPanelChange: onEndPanelChange, disabledDate: disabledEndDate,
                  getPopupContainer,
                }}
              />
            )}
          </div>
        </div>
      </div>
      <ModalActions canApply={Boolean(canApply)} onReset={handleReset} onApply={handleApply} />
    </div>
  );
};
